'use client';

/**
 * 【限时优惠弹窗 v6.98.0】未登录访客 — 注册赠 45 分钟免费试课（30 天有效）
 *
 * 产品背景（老板 2026-10-03 需求）：
 * - 未注册/未登录用户访问 /dongniai 时弹窗展示注册权益
 * - 后端权益已存在：fa_dongniai_settings.gift_minutes_on_register = 45,
 *   gift_expiry_days = 30；注册钩子 (User.php:49-66) 自动发放到
 *   fa_dongniai_gift_minutes。本组件纯展示层，零后端改动。
 *
 * 大厂 UX 规范（对齐淘宝/京东/网易云课堂营销弹窗）：
 * - 延迟 1.2s 弹出（等 auth status + 页面 hydration 完成，避免闪烁）
 * - 弹出动画：缩放 + 淡入（spring 物理曲线），关闭淡出
 * - 「放弃资格」→ 关闭 + localStorage 记忆 7 天不再弹（sessionStorage 会话级太激进，永久太激进）
 * - 「去注册领取」→ 跳注册页（带 return_url 登录后回跳）
 * - PC/手机自适应：max-width 400px + width calc(100vw - 32px)
 * - 遮罩 z-index 9999 压过 mobile-shell (9998)
 * - 无障碍：role=dialog aria-modal，Esc 可关闭
 * - 不影响 SEO/爬虫：初始不渲染，纯客户端条件渲染
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { X, Gift, Clock, ShieldCheck, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'dongniai_trial_popup_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 放弃资格后 7 天不再弹
const SHOW_DELAY_MS = 1200;

export function TrialOfferPopup() {
  const [open, setOpen] = useState(false);
  // 【v6.98.2】portal 挂载标记：SSR 阶段不渲染 portal，客户端 hydration 后再挂 body
  const [mounted, setMounted] = useState(false);
  // 【v6.98.1】赠礼配置读后台 fa_dongniai_settings（不硬编码，老板 2026-10-04 要求）
  const [giftMinutes, setGiftMinutes] = useState(45);
  const [giftDays, setGiftDays] = useState(30);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // 1) localStorage 已选择"放弃资格"且未过期 → 不弹
    try {
      const dismissed = window.localStorage.getItem(STORAGE_KEY);
      if (dismissed && Date.now() - Number(dismissed) < DISMISS_TTL_MS) {
        return;
      }
    } catch {
      /* localStorage 不可用（隐私模式等）→ 照常弹 */
    }

    // 2) 延迟弹出，等 /api/auth/status 返回真实登录态
    const timer = window.setTimeout(async () => {
      // 2a) 拉取后台赠礼配置（失败保持默认 45/30，不阻塞弹出）
      try {
        const cfgRes = await fetch('/dongniai_public.php?method=giftConfig', { cache: 'no-store' });
        if (cfgRes.ok) {
          const cfg = (await cfgRes.json()) as { success?: boolean; data?: { minutes?: number; days?: number } };
          if (cfg?.success && cfg.data) {
            if (typeof cfg.data.minutes === 'number' && cfg.data.minutes > 0) setGiftMinutes(cfg.data.minutes);
            if (typeof cfg.data.days === 'number' && cfg.data.days > 0) setGiftDays(cfg.data.days);
          }
        }
      } catch {
        /* 配置拉取失败 → 用默认 45/30 */
      }
      try {
        const res = await fetch('/api/auth/status/', { cache: 'no-store' });
        const data = (await res.json()) as { loggedIn?: boolean };
        if (data?.loggedIn) return; // 已登录 → 永不弹
      } catch {
        /* auth 检查失败 → 保守不弹（避免对已登录用户骚扰） */
        return;
      }
      setOpen(true);
    }, SHOW_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, []);

  const dismiss = useCallback(() => {
    setOpen(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  }, []);

  const goRegister = useCallback(() => {
    // 绝对路径（basePath 铁律：裸 a/location 必须显式含完整路径）
    window.location.href =
      window.location.origin + '/index/user/register.html?url=' + encodeURIComponent('/dongniai/');
  }, []);

  // 【v6.98.2 老板需求】已注册用户 → 去登录（同样带回跳）
  const goLogin = useCallback(() => {
    window.location.href =
      window.location.origin + '/index/user/login.html?url=' + encodeURIComponent('/dongniai/');
  }, []);

  // Esc 关闭 = 放弃资格（同记忆策略）
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismiss]);

  // 【v6.98.2 修正】AnimatePresence 必须整体放进 portal 内部：
  // 之前把 createPortal(...) 作为 AnimatePresence 的直接子级——AnimatePresence 要求直接子级是
  // 带 key 的 motion 元素，Portal 对象不满足 → React 19 + motion 下整棵不渲染（弹窗消失 P0）。
  // 正确结构：mounted && createPortal(<AnimatePresence>{open && <motion.div/>}</AnimatePresence>, body)
  if (!mounted) return null;
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="trial-offer-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 flex items-center justify-center px-4"
          style={{ zIndex: 9999, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
          role="dialog"
          aria-modal="true"
          aria-label={`限时优惠：注册赠送${giftMinutes}分钟免费试课`}
          onClick={dismiss}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 12 }}
            transition={{ type: 'spring', stiffness: 320, damping: 26 }}
            className="relative w-full max-w-[400px] overflow-hidden rounded-3xl bg-white shadow-2xl"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {/* 关闭按钮（v6.98.4 老板需求：右上角 ✕ 醒目可见，样式对齐主站 trial-close）。
                之前 bg-white/20 半透明底在紫色渐变上几乎隐形（截图实测看不见），
                且按钮在渐变 header 的 DOM 之前，同层级后来居上导致视觉被渐变盖住。 */}
            <button
              onClick={dismiss}
              aria-label="关闭"
              className="absolute right-3 top-3 z-10 flex size-8 items-center justify-center rounded-full border border-white/40 bg-black/25 text-white shadow-md transition hover:bg-black/40 active:scale-95"
            >
              <X className="size-4" strokeWidth={2.5} />
            </button>

            {/* 头部渐变区：品牌靛蓝渐变 + 装饰光斑 */}
            <div className="relative bg-gradient-to-br from-[#4F46E5] via-[#6366F1] to-[#818CF8] px-6 pb-7 pt-8 text-center text-white">
              {/* 装饰光斑（大厂弹窗常见柔光装饰） */}
              <div
                className="pointer-events-none absolute -left-10 -top-10 size-32 rounded-full bg-white/15 blur-2xl"
                aria-hidden
              />
              <div
                className="pointer-events-none absolute -bottom-14 -right-8 size-36 rounded-full bg-[#10B981]/25 blur-2xl"
                aria-hidden
              />

              <div className="mx-auto mb-3 flex size-14 items-center justify-center rounded-2xl bg-white/20 shadow-inner backdrop-blur-sm">
                <Gift className="size-7" />
              </div>
              <div className="mb-1 inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 text-[11px] font-semibold tracking-wide">
                <Sparkles className="size-3" />
                限时优惠
              </div>
              <h2 className="text-[22px] font-extrabold leading-snug tracking-tight">
                注册即赠
                <span className="mx-1 inline-block rounded-lg bg-[#FDE68A] px-1.5 py-0.5 text-[#4C1D95]">
                  {giftMinutes} 分钟
                </span>
                免费试课
              </h2>
              <p className="mt-1.5 text-[13px] font-medium text-white/85">
                AI 一对一辅导 · 注册立得到账
              </p>
            </div>

            {/* 权益说明区 */}
            <div className="space-y-2.5 px-6 py-5">
              <div className="flex items-center gap-3 rounded-xl bg-indigo-50/70 px-3.5 py-2.5">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
                  <Clock className="size-4 text-[#4F46E5]" />
                </div>
                <p className="text-[13px] font-medium text-slate-700">
                  {giftMinutes} 分钟免费时长，<span className="font-bold text-[#4F46E5]">{giftDays} 天内有效</span>
                </p>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-emerald-50/70 px-3.5 py-2.5">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
                  <ShieldCheck className="size-4 text-[#10B981]" />
                </div>
                <p className="text-[13px] font-medium text-slate-700">
                  免费时长课中自动抵扣，<span className="font-bold text-[#10B981]">无需绑卡</span>
                </p>
              </div>
            </div>

            {/* 底部按钮区 */}
            <div className="space-y-2.5 px-6 pb-6">
              <button
                onClick={goRegister}
                className="w-full rounded-xl bg-gradient-to-r from-[#4F46E5] to-[#6366F1] py-3 text-[15px] font-bold text-white shadow-lg shadow-indigo-500/30 transition-transform active:scale-[0.98]"
              >
                去注册领取
              </button>
              {/* 【v6.98.2 老板需求】已注册用户入口 */}
              <button
                onClick={goLogin}
                className="w-full rounded-xl border border-indigo-200 bg-white py-2.5 text-[14px] font-semibold text-[#4F46E5] transition-colors hover:border-indigo-300 hover:bg-indigo-50 active:scale-[0.98]"
              >
                已注册，去登录
              </button>
              <button
                onClick={dismiss}
                className="w-full rounded-xl py-2 text-[13px] font-medium text-slate-400 transition hover:text-slate-600"
              >
                放弃资格
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

